import React, { useEffect, useMemo, useState } from "react";

import SyDialog from "../../../components/SyDialog";
import { formatMoney, moneyOf, parseAmount, sanitizeAmountInput } from "../financeMoney";
import { AMOUNT_ERRORS } from "../financeCopy";

export const AMOUNT_SHEET_PRESETS = {
  "top-up": {
    title: "Top Up Wallet",
    balanceLabel: "Current Balance",
    submitLabel: "Continue to Payment",
    helper: "Top-ups are processed by Paystack in GHS.",
  },
  "withdraw-finance": {
    title: "Withdraw Funds",
    balanceLabel: "Available Balance",
    submitLabel: "Choose Account",
    helper: "A withdrawal fee may apply — you will see it before you confirm.",
  },
  "withdraw-accounts": {
    title: "Withdrawal Amount",
    balanceLabel: "Available Balance",
    submitLabel: "Set Amount",
    helper: null,
  },
};

const AmountEntrySheet = ({
  open,
  mode = "top-up",
  balance,
  minimumAmount,
  maximumAmount,
  busy = false,
  onClose,
  onSubmit,
}) => {
  const [raw, setRaw] = useState("");
  const preset = AMOUNT_SHEET_PRESETS[mode] || AMOUNT_SHEET_PRESETS["top-up"];
  const currency = balance?.currency;

  useEffect(() => {
    if (!open) setRaw("");
  }, [open]);

  const value = useMemo(() => parseAmount(raw), [raw]);

  const error = useMemo(() => {
    if (!raw.trim()) return null;
    if (!(value > 0)) return AMOUNT_ERRORS.zero;
    if (minimumAmount != null && value < minimumAmount) {
      return AMOUNT_ERRORS.min(moneyOf(minimumAmount, currency));
    }
    if (maximumAmount != null && value > maximumAmount) {
      return AMOUNT_ERRORS.max(moneyOf(maximumAmount, currency));
    }

    return null;
  }, [raw, value, minimumAmount, maximumAmount, currency]);

  const canSubmit = value > 0 && !error && !busy;

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    onSubmit(value);
  };

  return (
    <SyDialog open={open} title={preset.title} size="sm" onClose={onClose}>
      <form className="fin-amount" onSubmit={handleSubmit}>
        <div className="fin-amount-balance">
          <span>{preset.balanceLabel}</span>
          <b>{formatMoney(balance)}</b>
        </div>

        <label className="fin-field">
          <span className="fin-field-label">Amount</span>

          <div className="fin-amount-input">
            <span className="fin-amount-currency">{currency}</span>

            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={raw}
              onChange={(event) => setRaw(sanitizeAmountInput(event.target.value))}
            />
          </div>
        </label>

        {error && <p className="fin-field-error">{error}</p>}
        {!error && preset.helper && <p className="fin-field-hint">{preset.helper}</p>}

        <button
          type="submit"
          className="sy-flow-btn sy-flow-btn-primary fin-amount-submit"
          disabled={!canSubmit}
        >
          {busy ? "Working…" : preset.submitLabel}
        </button>
      </form>
    </SyDialog>
  );
};

export default AmountEntrySheet;
